import React, { useState, useRef } from "react";
import { Button } from "@mui/material";
import type { SubmitResponse } from "../types";
import styles from "../SkillGapAssessment.module.scss";
import KeyHighlightsPanel from "./KeyHighlightsPanel";
import SkillScoreSummary from "./SkillScoreSummary";
import { downloadJson, downloadPdf } from "../download";

interface ResultsStepProps {
  result: SubmitResponse;
  onReset: () => void;
  hideActions?: boolean;
}

// Helper to normalize both response shapes
function pickPayload(data: SubmitResponse) {
  if (data.result?.data) {
    // Wrapped shape
    return {
      ...data.result.data,
      gap_breakdown: data.result.data.gap_breakdown || [],
      role_matches: data.result.data.role_matches || [],
      extracted_skills: data.result.data.extracted_skills || [],
    };
  }
  // Flat shape
  return {
    role: data.role || "",
    benchmark_version: data.benchmark_version || "",
    skills: data.skills || [],
    summary: data.summary || {
      exceeds_target: 0,
      meets_target: 0,
      minor_gap: 0,
      moderate_gap: 0,
      significant_gap: 0,
    },
    prioritized_gaps: data.prioritized_gaps || [],
    gap_breakdown: data.gap_breakdown || [],
    role_matches: data.role_matches || [],
    extracted_skills: data.extracted_skills || [],
  };
}


export default function ResultsStep({
  result,
  onReset,
  hideActions = false,
}: ResultsStepProps): JSX.Element {
  const payload = pickPayload(result);
  const [downloading, setDownloading] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  const workflowId = result.workflow_id || "unknown";

  return (
    <div>
      <div ref={resultsRef}>
        <div className={styles.resultsHeader}>
          <h2 className={styles.resultsTitle}>Assessment Results</h2>
          <div className={styles.resultsSubtitle}>
            {payload.role && (
              <span className={styles.resultsMeta}>Role: {payload.role}</span>
            )}
            {payload.benchmark_version && (
              <span className={styles.resultsMeta}>v{payload.benchmark_version}</span>
            )}
          </div>
        </div>

        {/* Summary strip */}
        <div className={styles.summaryStrip}>
          <div className={`${styles.summaryItem} ${styles.summaryExceeds}`}>
            <div className={styles.count}>{payload.summary.exceeds_target}</div>
            <div className={styles.label}>Exceeds Target</div>
          </div>
          <div className={`${styles.summaryItem} ${styles.summaryMeets}`}>
            <div className={styles.count}>{payload.summary.meets_target}</div>
            <div className={styles.label}>Meets Target</div>
          </div>
          <div className={`${styles.summaryItem} ${styles.summaryMinor}`}>
            <div className={styles.count}>{payload.summary.minor_gap}</div>
            <div className={styles.label}>Minor Gap</div>
          </div>
          <div className={`${styles.summaryItem} ${styles.summaryModerate}`}>
            <div className={styles.count}>{payload.summary.moderate_gap}</div>
            <div className={styles.label}>Moderate Gap</div>
          </div>
          <div className={`${styles.summaryItem} ${styles.summaryCritical}`}>
            <div className={styles.count}>{payload.summary.significant_gap}</div>
            <div className={styles.label}>Significant Gap</div>
          </div>
        </div>

      {payload.skills && payload.skills.length > 0 && (
        <>
          <KeyHighlightsPanel skills={payload.skills} />
          <SkillScoreSummary skills={payload.skills} />
        </>
      )}
      </div>

      {!hideActions && (
        <div className={styles.resultsActions}>
          <Button variant="outlined" onClick={onReset}>
            Start Over
          </Button>
          <div className={styles.resultsDownloads}>
            <Button
              variant="outlined"
              onClick={() => downloadJson(result, workflowId)}
              disabled={downloading}
            >
              Download JSON
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (!resultsRef.current) return;
                setDownloading(true);
                try {
                  await downloadPdf(resultsRef.current, workflowId);
                } finally {
                  setDownloading(false);
                }
              }}
              disabled={downloading}
            >
              Download PDF
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
